/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Switch, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../../../shared/theme/ticketColors'
import { spacing } from '../../../../shared/theme/spacing'
import { typography } from '../../../../shared/theme/typography';
import type { BranchPermission } from '../../../../shell/session/store/sessionTypes';
import { actionLabelEs, customizableActionsForModule } from '../../../../shell/session/permissions/permissionCatalog';
import type { PermissionLevel } from '../../../store/useEmployeesStore';
import { permissionLevelFromTriple } from '../../../store/useEmployeesStore';
import { MODULE_LABELS_ES } from './permissionUiLabels';
import { levelsForModule } from './permissionLevelOptions';

type Props = {
  perm: BranchPermission;
  activeBranchId: string;
  isLast: boolean;
  expanded: boolean;
  customizeOpen: boolean;
  canEdit: boolean;
  onToggleExpand: () => void;
  onToggleCustomize: () => void;
  onSelectLevel: (level: PermissionLevel) => void;
  onToggleAgenda: (field: 'canManageAgenda' | 'canViewOthers') => void;
  onSetAction: (action: string, enabled: boolean) => void;
};

export function EmployeePermissionModuleRow({
  perm,
  activeBranchId,
  isLast,
  expanded,
  customizeOpen,
  canEdit,
  onToggleExpand,
  onToggleCustomize,
  onSelectLevel,
  onToggleAgenda,
  onSetAction,
}: Props) {
  const moduleLabel = MODULE_LABELS_ES[perm.module] ?? perm.module;
  const currentLevel = permissionLevelFromTriple({
    canView: perm.canView,
    canEdit: perm.canEdit,
    canVoid: perm.canVoid ?? false,
    canDiscount: perm.canDiscount ?? false,
    canManageAgenda: perm.canManageAgenda ?? false,
  });
  const options = levelsForModule(perm.module);
  const currentOpt = options.find((o) => o.level === currentLevel) ?? options[0];
  const customActions = customizableActionsForModule(perm.module);
  const hasCustom = customActions.length > 0 && currentLevel !== 'none';
  const customActionCount = customActions.filter((action) => perm.actions?.[action] === true).length;

  return (
    <View
      key={`${activeBranchId}-${perm.module}`}
      style={[styles.permBlock, !isLast && styles.permBlockBorder]}
    >
      <TouchableOpacity
        style={styles.permTrigger}
        onPress={onToggleExpand}
        accessibilityRole="button"
        accessibilityLabel={`${moduleLabel}: ${currentOpt.label}`}
        accessibilityState={{ expanded }}
        disabled={!canEdit && !expanded}
      >
        <View style={styles.permNameRow}>
          <Text style={styles.permModuleName}>{moduleLabel}</Text>
          {customActionCount > 0 ? (
            <View style={styles.customBadge} testID="perfil.team.detail.module-custom-badge">
              <Text style={styles.customBadgeText}>
                {customActionCount} {customActionCount === 1 ? 'acción' : 'acciones'}
              </Text>
            </View>
          ) : null}
        </View>
        <View style={styles.permValue}>
          <Text
            style={[styles.permValueText, currentLevel !== 'none' && styles.permValueTextActive]}
          >
            {currentOpt.shortLabel}
          </Text>
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={16}
            color={ticketColors.ink3}
          />
        </View>
      </TouchableOpacity>

      {expanded && canEdit ? (
        <View style={styles.levelList}>
          {options.map((opt) => {
            const selected = opt.level === currentLevel;
            return (
              <TouchableOpacity
                key={opt.level}
                style={[styles.levelRow, selected && styles.levelRowActive]}
                onPress={() => onSelectLevel(opt.level)}
                accessibilityRole="button"
                accessibilityLabel={opt.label}
                accessibilityState={{ selected }}
              >
                <Text style={[styles.levelRowText, selected && styles.levelRowTextActive]}>
                  {opt.label}
                </Text>
                {selected ? (
                  <Ionicons name="checkmark" size={16} color={ticketColors.gold} />
                ) : null}
              </TouchableOpacity>
            );
          })}

          {perm.module === 'Agenda' && currentLevel !== 'none' ? (
            <>
              <View style={styles.agendaManageRow}>
                <View style={styles.agendaManageCopy}>
                  <Text style={styles.agendaManageTitle}>Administrar Agenda</Text>
                  <Text style={styles.agendaManageHint}>
                    Horarios, ausencias, servicios y recursos.
                  </Text>
                </View>
                <Switch
                  value={perm.canManageAgenda === true}
                  onValueChange={() => onToggleAgenda('canManageAgenda')}
                  trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
                  thumbColor={ticketColors.paper}
                  accessibilityLabel="Administrar Agenda"
                />
              </View>
              <View style={styles.agendaManageRow}>
                <View style={styles.agendaManageCopy}>
                  <Text style={styles.agendaManageTitle}>Ver agendas de otros</Text>
                  <Text style={styles.agendaManageHint}>
                    Si está apagado, solo ve turnos de su recurso vinculado.
                  </Text>
                </View>
                <Switch
                  value={perm.canViewOthers !== false}
                  onValueChange={() => onToggleAgenda('canViewOthers')}
                  trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
                  thumbColor={ticketColors.paper}
                  accessibilityLabel="Ver agendas de otros"
                />
              </View>
            </>
          ) : null}

          {perm.module === 'Commissions' && currentLevel !== 'none' ? (
            <View style={styles.agendaManageRow}>
              <View style={styles.agendaManageCopy}>
                <Text style={styles.agendaManageTitle}>Ver comisiones de otros</Text>
                <Text style={styles.agendaManageHint}>
                  Si está apagado, solo ve sus propias comisiones.
                </Text>
              </View>
              <Switch
                value={perm.canViewOthers !== false}
                onValueChange={() => onToggleAgenda('canViewOthers')}
                trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
                thumbColor={ticketColors.paper}
                accessibilityLabel="Ver comisiones de otros"
              />
            </View>
          ) : null}

          {hasCustom ? (
            <View style={styles.customizeBlock}>
              <TouchableOpacity
                style={styles.customizeTrigger}
                onPress={onToggleCustomize}
                accessibilityRole="button"
                accessibilityLabel={`Personalizar ${moduleLabel}`}
                accessibilityState={{ expanded: customizeOpen }}
              >
                <Text style={styles.customizeTitle}>Personalizar</Text>
                <Ionicons
                  name={customizeOpen ? 'chevron-up' : 'chevron-down'}
                  size={14}
                  color={ticketColors.ink3}
                />
              </TouchableOpacity>
              {customizeOpen
                ? customActions.map((action) => {
                    const on = perm.actions?.[action] === true;
                    return (
                      <View key={action} style={styles.actionRow}>
                        <Text style={styles.actionLabel} numberOfLines={2}>
                          {actionLabelEs(action)}
                        </Text>
                        <Switch
                          value={on}
                          onValueChange={(enabled) => onSetAction(action, enabled)}
                          trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
                          thumbColor={ticketColors.paper}
                          accessibilityLabel={actionLabelEs(action)}
                        />
                      </View>
                    );
                  })
                : null}
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  permBlock: {
    paddingVertical: spacing.xs,
  },
  permBlockBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  permTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    minHeight: 44,
  },
  permNameRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
    paddingRight: spacing.sm,
  },
  permModuleName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
    fontSize: 14,
  },
  customBadge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: ticketColors.gold + '20',
  },
  customBadgeText: {
    ...typography.caption,
    fontSize: 10,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  permValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  permValueText: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
  },
  permValueTextActive: {
    color: ticketColors.ink,
  },
  levelList: {
    paddingHorizontal: spacing.xs,
    paddingBottom: spacing.sm,
    gap: 4,
  },
  levelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.sm,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    minHeight: 44,
  },
  levelRowActive: {
    borderColor: ticketColors.gold + '50',
    backgroundColor: ticketColors.gold + '10',
  },
  levelRowText: {
    ...typography.body,
    color: ticketColors.ink2,
    fontSize: 14,
    fontWeight: '500',
  },
  levelRowTextActive: {
    color: ticketColors.gold,
    fontWeight: '700',
  },
  agendaManageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginTop: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
  },
  agendaManageCopy: {
    flex: 1,
    gap: 2,
  },
  agendaManageTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 13,
  },
  agendaManageHint: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 16,
  },
  customizeBlock: {
    marginTop: spacing.xs,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  customizeTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.sm,
    paddingVertical: 10,
    backgroundColor: ticketColors.paper2,
  },
  customizeTitle: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    minHeight: 44,
  },
  actionLabel: {
    ...typography.body,
    color: ticketColors.ink,
    fontSize: 13,
    flex: 1,
  },
});
